import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

export type PriceScopeTab = "global" | "tables" | "clients";
type PriceTableRow = { id: string; name: string; adjustmentPercent: number; partnerCount: number };
type PartnerRow = { partnerId: string; clientOrganizationId: string; name: string; priceTableId: string | null };

const TABS: { key: PriceScopeTab; label: string }[] = [
  { key: "global", label: "Catálogo Base" },
  { key: "tables", label: "Tabelas (Convênios/Parcerias)" },
  { key: "clients", label: "Exceções por Clínica" },
];

type Props = {
  activeTab: PriceScopeTab;
  onTabChange: (tab: PriceScopeTab) => void;
  priceTables: PriceTableRow[];
  selectedTableId: string | null;
  onSelectTable: (id: string) => void;
  individualPartners: PartnerRow[];
  selectedClientId: string | null;
  onSelectClient: (id: string) => void;
  locked: boolean;
};

/** Seletor de escopo da tela de Preços — abas explícitas em vez de um dropdown único misturando
 *  tudo, cada uma com seu próprio sub-seletor (chips de tabela / dropdown de clínica). */
export function PriceScopeTabs({
  activeTab,
  onTabChange,
  priceTables,
  selectedTableId,
  onSelectTable,
  individualPartners,
  selectedClientId,
  onSelectClient,
  locked,
}: Props) {
  const utils = trpc.useUtils();
  const [creatingTable, setCreatingTable] = useState(false);
  const [newTableName, setNewTableName] = useState("");

  const createTable = trpc.labPricing.priceTables.create.useMutation({
    onSuccess: (table) => {
      utils.labPricing.priceTables.list.invalidate();
      toast.success("Tabela criada.");
      setCreatingTable(false);
      setNewTableName("");
      onSelectTable(table.id);
    },
    onError: (err) => toast.error(err.message || "Erro ao criar tabela."),
  });

  const handleCreateTable = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTableName.trim()) return;
    createTable.mutate({ name: newTableName.trim(), adjustmentPercent: 0 });
  };

  return (
    <div>
      <div
        data-tour="catalogo-gerenciar-precos-abas"
        className="flex flex-wrap items-center gap-1 border-b border-zinc-200 dark:border-border"
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onTabChange(tab.key)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors",
              activeTab === tab.key
                ? "border-brand-600 text-brand-700 dark:border-primary dark:text-primary"
                : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "tables" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {priceTables.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelectTable(t.id)}
              className={cn(
                "h-8 rounded-full border px-3 text-[12.5px] font-medium transition-colors",
                t.id === selectedTableId
                  ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                  : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted",
              )}
            >
              {t.name}
            </button>
          ))}
          {priceTables.length === 0 && !creatingTable && (
            <span className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">
              Nenhuma tabela criada ainda.
            </span>
          )}
          {!locked && !creatingTable && (
            <button
              type="button"
              onClick={() => setCreatingTable(true)}
              title="Criar nova tabela de preço"
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-dashed border-zinc-300 px-3 text-[12.5px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5" />
              Nova tabela
            </button>
          )}
        </div>
      )}

      {activeTab === "tables" && creatingTable && (
        <form
          onSubmit={handleCreateTable}
          className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 dark:border-border dark:bg-background/40"
        >
          <input
            autoFocus
            value={newTableName}
            onChange={(e) => setNewTableName(e.target.value)}
            placeholder="Ex: Convênio Premium"
            className="h-9 min-w-[180px] flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
          />
          <button
            type="submit"
            disabled={createTable.isPending || !newTableName.trim()}
            className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            Criar
          </button>
          <button
            type="button"
            onClick={() => {
              setCreatingTable(false);
              setNewTableName("");
            }}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
        </form>
      )}

      {activeTab === "clients" && (
        <div className="mt-3">
          {individualPartners.length === 0 ? (
            <p className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">
              Nenhuma clínica parceira com config individual — clínicas vinculadas a uma tabela
              aparecem na aba "Tabelas".
            </p>
          ) : (
            <select
              value={selectedClientId ?? ""}
              onChange={(e) => onSelectClient(e.target.value)}
              className="h-9 w-full max-w-xs rounded-md border border-zinc-200 bg-white px-2.5 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="">Selecione uma clínica...</option>
              {individualPartners.map((p) => (
                <option key={p.clientOrganizationId} value={p.clientOrganizationId}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
